import React from 'react';
import type { QuestionFormProps } from '../../types';
import { Button } from '../ui/button';
import { Card, CardContent, CardFooter } from '../ui/card';

const QuestionForm: React.FC<QuestionFormProps> = ({
  question,
  onChange,
  onDelete,
}) => {
  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({
      ...question,
      text: e.target.value,
    });
  };

  const handleOptionTextChange = (index: number, value: string) => {
    const updatedOptions = [...question.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      text: value,
    };
    
    onChange({
      ...question,
      options: updatedOptions,
    });
  };

  const handleOptionCorrectChange = (index: number, isCorrect: boolean) => {
    const updatedOptions = [...question.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      isCorrect,
    };
    
    onChange({
      ...question,
      options: updatedOptions,
    });
  };

  const handleTimeLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 5) {
      onChange({
        ...question,
        timeLimit: value,
      });
    }
  };

  const handlePointValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      onChange({
        ...question,
        pointValue: value,
      });
    }
  };

  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">
              Question
            </label>
            <textarea
              className="w-full p-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-20 transition-all"
              value={question.text}
              onChange={handleQuestionTextChange}
              placeholder="Enter your question"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">
                Time Limit (seconds)
              </label>
              <input
                type="number"
                min="5"
                className="w-full h-12 px-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                value={question.timeLimit}
                onChange={handleTimeLimitChange}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">
                Point Value
              </label>
              <input
                type="number"
                min="1"
                className="w-full h-12 px-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                value={question.pointValue}
                onChange={handlePointValueChange}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Answer Options (select at least one correct answer)
            </label>
            
            <div className="space-y-3">
              {question.options.map((option, index) => (
                <div key={index} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id={`option-${index}-correct`}
                    checked={option.isCorrect}
                    onChange={(e) => handleOptionCorrectChange(index, e.target.checked)}
                    className="h-5 w-5 rounded border-2 border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="text"
                    className="flex-grow h-12 px-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    value={option.text}
                    onChange={(e) => handleOptionTextChange(index, e.target.value)}
                    placeholder={`Option ${String.fromCharCode(65 + index)}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="justify-end border-t pt-4">
        <Button variant="destructive" onClick={onDelete} type="button">
          Delete Question
        </Button>
      </CardFooter>
    </Card>
  );
};

export default QuestionForm;